import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const BaliPass = () => {
  const tourData = {
    name: "Bali Pass Trek",
    duration: "07 Nights 08 Days",
    price: "₹ 18,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935855/nw18bwxvx2qhtvpiwim9.webp",
    altitude: "16,000 ft",
    difficulty: "Difficult",
    region: "Uttarakhand",
    highlights: ["High altitude", "Challenging trek", "Mountain views"],
    bestTime: "May to October",
    description: "The Bali Pass Trek is one of the most challenging and rewarding treks in Uttarakhand, taking you to an altitude of 16,000 feet. This difficult trek offers stunning mountain views and is perfect for experienced trekkers looking for an ultimate adventure in the Himalayas."
  };

  return (
    <>
      <Helmet>
        <title>Bali Pass Trek - NomadTrailz</title>
        <meta name="description" content="Experience the challenging Bali Pass Trek in Uttarakhand. High altitude trek with stunning mountain views and ultimate adventure." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              High altitude trek with stunning mountain views and ultimate adventure
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek takes you through the challenging Bali Pass, one of the most 
                    demanding treks in the Himalayas. The journey offers spectacular views 
                    of snow-capped peaks, high-altitude lakes, and pristine wilderness. 
                    It's a perfect choice for experienced trekkers seeking the ultimate 
                    mountain adventure.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>High altitude adventure</li>
                    <li>Challenging trek experience</li>
                    <li>Stunning mountain views</li>
                    <li>Ruinsara Tal lake</li>
                    <li>Bali Pass crossing</li>
                    <li>Yamunotri temple visit</li>
                    <li>Wildlife sanctuary exploration</li>
                    <li>Ultimate mountain challenge</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: From Dehradun to Sankri</h4>
                    <ul>
                      <li>Pickup from Dehradun in the morning for a 240 km (10 hours) lengthy but beautiful trip to Sankri through Mussoorie, Barkot, Purola, Mori, and Netwar along the Tons River.</li>
                      <li>Located in the Uttarkashi district, on the slopes of the Himalayas, lies the peaceful village- Sankri.</li>
                      <li>In addition to several other well-known treks, like the Kedarkantha Trek, Har ki Dun, Rupin Pass, Bali Pass, and countless more, this modest community serves as the base camp for these renowned treks.</li>
                      <li>At an elevation of 6,400 feet above sea level, this community is situated within the Govind wildlife sanctuary.</li>
                      <li>It is an ideal spot for those seeking a serene mountain vacation or those who are adventurous.</li>
                      <li>This location has a breathtaking view of the Himalayas and a wealth of cultural heritage, thanks to its wooden construction.</li>
                      <li>Spend the night in Sankri and have dinner there.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: From Sankri to Osla</h4>
                    <ul>
                      <li>Today morning, after breakfast, In order to reach Osla from Sankri, you will have to trek roughly 26 km, during which time you will pass through verdant woods, meadows, and streams.</li>
                      <li>The route is well defined and travels through picturesque settings, providing beautiful views of the mountains that are located in the surrounding area.</li>
                      <li>You should make sure that you have adequate water, food, and basic hiking equipment with you on your adventure.</li>
                      <li>The trek offers stunning views of the surrounding peaks and valleys throughout the journey.</li>
                      <li>Osla village provides a perfect resting spot with basic accommodation facilities.</li>
                      <li>Evening time can be spent exploring the local culture and traditions of the mountain village.</li>
                      <li>Overnight stay in Osla with dinner and rest for the next day's challenging trek.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: From Osla to Rainbasera</h4>
                    <ul>
                      <li>After eating breakfast, begin your journey to rainbasera, which is located 18 km away.</li>
                      <li>About four to five hours will be required for this challenging trek through varied terrain.</li>
                      <li>It is planned to establish up camp along the banks of the Supin River.</li>
                      <li>The evening and night were spent in tents with beautiful river views.</li>
                      <li>The campsite offers a perfect setting for acclimatization before the higher altitude sections.</li>
                      <li>Enjoy the peaceful sounds of the flowing river and prepare for the upcoming challenges.</li>
                      <li>Rest and recuperate for the next day's trek to Ruinsara Tal.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: From Rainbasera to Ruinsara Tal and back</h4>
                    <ul>
                      <li>Today we will trek to ruinsara tal. It will be a path that is comparable to the one that you saw the day before.</li>
                      <li>Keep walking for around three to four hours in order to reach the body of water.</li>
                      <li>It is possible to reach the lake via traversing streams, trekking, or engaging in other exciting activities.</li>
                      <li>This day is like something out of a dream. The location is situated between the Supin river and the Ruinsara lake, and it is bordered by meadows that are sprinkled with enormous mountains that are covered in snow.</li>
                      <li>Kala Nag, Bandarpoonch, and Dhumdhar Khai are just few of the breathtaking peaks that are part of the Bandarpoonch range that dominate the sky.</li>
                      <li>You will also locate a temple in this location. Taking in the sights is as easy as going for a stroll around the neighbourhood.</li>
                      <li>After some time descend back to rainbasera and stay there for overnight.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: From Rainbasera to Odari</h4>
                    <ul>
                      <li>It will just take you four kilometres to complete today's trek. In the direction of the Supin river, it falls.</li>
                      <li>This journey will take you through wooded areas and across rivers.</li>
                      <li>There is another ascent on the trail before arriving to the subsequent meadow.</li>
                      <li>From the perspective of the meadows, the higher peaks are visible. This is a sneak peek into the things that you have witnessed over the course of the past several days.</li>
                      <li>It comes as a surprise that the greenery will grow less abundant in the future. Within a kilometer's distance is Odari.</li>
                      <li>The campground is located next to a rock cave. The cave with the name Odari was the inspiration for the name.</li>
                      <li>It is claimed that Bali, the brother of Lord Krishna, lived in this cave at one point in time. This cave also provides an excellent view of Swargrohini 1 and 2, which are located nearby.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 6: From Odari to Base Camp</h4>
                    <ul>
                      <li>In spite of the fact that it appears to be a shorter day, it is not simple. It is going to be a tough ascent up a slope that is slanted at sixty degrees.</li>
                      <li>This is a ridge trek that is fraught with scree and loose rocks. Stones that are dispersed throughout the path are another factor that contributes to the difficulty of the situation.</li>
                      <li>It is important that you receive adequate water and sleep. It is imperative that you take the required measures in order to avoid major issues that may arise from height increase.</li>
                      <li>The temperature typically falls below 0 degrees during the nighttime hours. Please get ready for the cold.</li>
                      <li>At the end of a hard day, you may take a break in tents.</li>
                      <li>This day requires careful navigation and following the trek leader's instructions precisely.</li>
                      <li>Prepare mentally and physically for the challenging Bali Pass crossing the next day.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 7: From Base Camp to Lower Dhamni</h4>
                    <ul>
                      <li>You may say that this is the peak day. Your journey to Lower Dhamni begins today with a traversal of the Bali Pass.</li>
                      <li>To make it through the long day, you must eat adequately. There is a lot of snow on the steep slope up to Bali Pass.</li>
                      <li>Stick on the path that your advisors have indicated. No roping up is required, however microspikes are an option.</li>
                      <li>It takes two or three hours of tough climbing to reach the pass's peak. In terms of width, it's barely 10 by 30 feet.</li>
                      <li>You will feel as if you are standing on top of the world at this point. Swargarohini and Banderpooch peaks may be seen from any angle.</li>
                      <li>Looking to the right, you can make see the Yamnotri Valley. Just sit back, relax, and take it all in.</li>
                      <li>The downhill portion is when the action really starts, so be sure to keep an eye out. Following a tight trail, you will descend nearly to a right angle.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 8: From Lower Dhamni to Yamunotri to Dehradun</h4>
                    <ul>
                      <li>Today is the last day of the trek. Today is going to be the easiest day, with a significant number of descents rather than ascents.</li>
                      <li>It is a walkway that is paved with cement. Additional one to two hours are required for the ascent to Yamunotri, where you can visit, explore and worship.</li>
                      <li>After few hours, we will depart for Dehradun and will reach there in the evening.</li>
                      <li>After reaching Dehradun our trekking journey ends. Every trekker can plan further days accordingly.</li>
                      <li>Visit the sacred Yamunotri temple and take blessings for completing this challenging trek.</li>
                      <li>Celebrate your achievement of completing one of the most difficult treks in the Himalayas.</li>
                      <li>Return to Dehradun with memories of an unforgettable mountain adventure.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">Essential items you should bring for a comfortable and safe trekking experience:</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Warm jackets and thermals</li>
                        <li>Waterproof trekking pants</li>
                        <li>Woolen socks and gloves</li>
                        <li>Rain gear and poncho</li>
                        <li>Quick dry t-shirts</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Sturdy trekking shoes</li>
                        <li>Trekking poles</li>
                        <li>Headlamp with extra batteries</li>
                        <li>Water bottles (2-3 liters)</li>
                        <li>Backpack with rain cover</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-user"></i> Personal Items</h4>
                      <ul>
                        <li>Personal toiletries</li>
                        <li>Medicines (if any)</li>
                        <li>Camera</li>
                        <li>Power bank</li>
                        <li>Snacks and energy bars</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Pick and Drop Transfers from/to Dehradun</li>
                    <li>All meals during the trek with proper hygiene parameters</li>
                    <li>Triple/double sharing tent accommodations</li>
                    <li>Morning and evening tea with refreshments during the trek</li>
                    <li>All tolls and forest fees</li>
                    <li>Trekking gears (tents, sleeping bags, ropes, gaiters, crampons etc.)</li>
                    <li>First Aid Medical Kit</li>
                    <li>Expert Guide and support staff</li>
                    <li>Camping equipment and logistics</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Food during pick and drop transfers</li>
                    <li>Porters/mules charges</li>
                    <li>Personal expenses</li>
                    <li>Insurance</li>
                    <li>Anything apart from the inclusions</li>
                    <li>GST 5%</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 6-8 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>16,000 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>65 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Difficult</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>May to October</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person</h4>
                  <ul>
                    <li><strong>All Sharing:</strong> ₹ 18,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Dehradun:</strong> Railway Station or Bus Stand between 6:00 AM to 7:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>High altitude adventure</li>
                    <li>Challenging trek experience</li>
                    <li>Stunning mountain views</li>
                    <li>Ruinsara Tal lake</li>
                    <li>Bali Pass crossing</li>
                    <li>Yamunotri temple visit</li>
                    <li>Wildlife sanctuary exploration</li>
                    <li>Ultimate mountain challenge</li>
                    <li>Expert guide and support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default BaliPass;
